import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DataAccessService } from '@tripointe/tripointe-cms/data-access';
import {
  DEFAULT_PAGINATION_OPTIONS,
  DEFAULT_SORTING_OPTIONS
} from '@tripointe/tripointe-cms/constants';
import {
  PaginatorOptions,
  DataListQueryParams,
  ListStatsCount,
  DataListConfig
} from '@tripointe/tripointe-cms/types';

import { ToastService } from './toast.service';
import { NavigationService } from './navigation.service';

@Injectable()
export class DataListService {
  paginatorOptions$ = new BehaviorSubject(DEFAULT_PAGINATION_OPTIONS);
  loading$ = new BehaviorSubject(false);
  sort$ = new BehaviorSubject<Sort>(DEFAULT_SORTING_OPTIONS);
  data$ = new BehaviorSubject<any[]>([]);
  filters$ = new BehaviorSubject<object>({});
  archived$ = new BehaviorSubject(false);
  statsCount$ = new BehaviorSubject<ListStatsCount>({ active: 0, archived: 0 });

  config: DataListConfig = null;

  additionalQueryParams: Object = null;
  paramsForFetchCountQuery: Object = null;

  constructor(
    private dataAccess: DataAccessService,
    private navigationService: NavigationService,
    private toast: ToastService
  ) {}

  private setLoading(isLoading: boolean): void {
    this.loading$.next(isLoading);
  }

  private setPagination(options: PaginatorOptions): void {
    this.paginatorOptions$.next(options);
  }

  private setSort(sort: Sort): void {
    this.sort$.next(sort);
  }

  private setData(data: any[]): void {
    this.data$.next(data);
  }

  private setFilters(filters: object): void {
    this.filters$.next(filters);
  }

  private setArchived(isArchived: boolean): void {
    this.archived$.next(isArchived);
  }

  private setStatsCount(stats: ListStatsCount): void {
    this.statsCount$.next(stats);
  }

  private getDefaultQueryOptions(): any {
    const { active: sortBy, direction: order } = this.sort$.value;
    const { pageSize: limit, pageIndex } = this.paginatorOptions$.value;
    const filters = this.filters$.value;
    const archived = this.archived$.value;

    return {
      sortBy,
      order,
      archived,
      limit,
      page: pageIndex + 1,
      ...filters
    };
  }

  private setFromQueryParams(): void {
    const filtersParams = this.navigationService.getQueryParams(DataListQueryParams.filters);
    const sortParams = this.navigationService.getQueryParams(DataListQueryParams.sort);
    const archivedParams = this.navigationService.getQueryParams(DataListQueryParams.archived);

    if (filtersParams) {
      this.setFilters(filtersParams);
    }

    if (sortParams) {
      this.setSort(sortParams as Sort);
    }

    this.setArchived(!!archivedParams);
  }

  init(
    config: DataListConfig,
    additionalQueryParams?: Object,
    paramsForFetchCountQuery?: Object
  ): void {
    const { skipLoader, ...rawConfig } = config;
    this.config = rawConfig;
    if (additionalQueryParams) this.additionalQueryParams = additionalQueryParams;
    if (paramsForFetchCountQuery) this.paramsForFetchCountQuery = paramsForFetchCountQuery;
    this.setFromQueryParams();
    this.loadData(this.getDefaultQueryOptions(), skipLoader);
    this.loadStats();
  }

  loadData(options: Object = this.getDefaultQueryOptions(), skipLoader?: boolean): void {
    if (!skipLoader) {
      this.setLoading(true);
    }

    const mergedOptions = { ...options, ...this.additionalQueryParams };
    this.dataAccess
      .get(this.config.dataDocument, mergedOptions)
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((res: ApolloQueryResult<any>) => {
        const data = res.data[this.config.dataAccessKey];
        if (data.error) {
          this.toast.error('An error occurred during data loading. Please try again.');

          return;
        }

        this.setData(data.items);

        this.setPagination({
          ...this.paginatorOptions$.value,
          length: data.meta?.totalItems
        });
      });
  }

  loadStats(): void {
    if (!this.config.statsDocument) {
      return;
    }
    this.dataAccess
      .get(this.config.statsDocument, this.paramsForFetchCountQuery)
      .subscribe((res: ApolloQueryResult<any>) => {
        const stats = res.data[this.config.statsAccessKey];
        this.setStatsCount(stats.statsCount);
      });
  }

  changeSort(sort: Sort): void {
    const paginatorOptions = this.paginatorOptions$.value;
    const { active: sortBy, direction: order } = sort;
    const filters = this.filters$.value;
    const archived = this.archived$.value;

    const queryOptions = {
      sortBy,
      order,
      archived,
      page: 1,
      limit: paginatorOptions.pageSize,
      ...filters
    };

    this.setSort(sort);
    this.setPagination({ ...paginatorOptions, pageIndex: 0, previousPageIndex: 0 });
    this.loadData(queryOptions);
    this.navigationService.updateQueryParams(DataListQueryParams.sort, sort);
  }

  changePagination(options: PaginatorOptions): void {
    const queryOptions = {
      ...this.getDefaultQueryOptions(),
      limit: options.pageSize,
      page: options.pageIndex + 1
    };

    this.setPagination(options);
    this.loadData(queryOptions);
  }

  changeFilters(filters: object): void {
    const paginatorOptions = this.paginatorOptions$.value;
    const { active: sortBy, direction: order } = this.sort$.value;
    const archived = this.archived$.value;

    const queryOptions = {
      order,
      sortBy,
      archived,
      page: 1,
      limit: paginatorOptions.pageSize,
      ...filters
    };

    this.setFilters(filters);
    this.setPagination({ ...paginatorOptions, pageIndex: 0, previousPageIndex: 0 });
    this.loadData(queryOptions);
    this.navigationService.updateQueryParams(DataListQueryParams.filters, filters);
  }

  changeArchive(archived: boolean): void {
    const queryOptions = { archived, limit: DEFAULT_PAGINATION_OPTIONS.pageSize };

    this.setArchived(archived);
    this.setPagination(DEFAULT_PAGINATION_OPTIONS);
    this.setFilters({});
    this.setSort(DEFAULT_SORTING_OPTIONS);

    this.navigationService.updateQueryParams(DataListQueryParams.archived, archived, false);
    this.loadData(queryOptions);
  }

  refreshDataAfterItemExclude(): void {
    const items = this.data$.value;
    const hasMoreItems = items.length > 1;
    const isFirstPage = this.paginatorOptions$.value.pageIndex === 0;

    this.loadStats();

    if (hasMoreItems || isFirstPage) {
      this.loadData();
    } else {
      this.switchToPreviousPage();
    }
  }

  switchToPreviousPage(): void {
    const options = this.getDefaultQueryOptions();
    const paginatorOptions = this.paginatorOptions$.value;

    this.setPagination({
      ...paginatorOptions,
      previousPageIndex: paginatorOptions.pageIndex,
      pageIndex: paginatorOptions.pageIndex - 1
    });

    this.loadData({ ...options, page: options.page - 1 });
  }
}
